import { useEffect, useRef, useMemo } from 'react'
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { StopDetails, RouteForStop, StopInfo } from '@/lib/api'

interface RouteWithStops {
  route: RouteForStop
  stops: StopInfo[]
}

interface StopInfoMapProps {
  stop: StopDetails
  routesWithStops: RouteWithStops[]
  lang: 'el' | 'en'
}

const stopIcon = new L.DivIcon({
  className: 'map-marker stop-marker',
  html: '<div class="marker-inner"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8]
})

const selectedStopIcon = new L.DivIcon({
  className: 'map-marker stop-marker selected',
  html: '<div class="marker-inner"></div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14]
})

// Generate distinct colors for routes
const ROUTE_COLORS = [
  '#3b82f6', // blue
  '#ef4444', // red
  '#22c55e', // green
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
]

function FitBounds({ stop, routesWithStops }: { stop: StopDetails; routesWithStops: RouteWithStops[] }) {
  const map = useMap()
  const hasFittedInitial = useRef(false)

  useEffect(() => {
    if (hasFittedInitial.current) return

    const stopLat = parseFloat(stop.stop_lat)
    const stopLng = parseFloat(stop.stop_lng)

    if (routesWithStops.length === 0) {
      map.setView([stopLat, stopLng], 16)
      hasFittedInitial.current = true
      return
    }

    // Collect all points from all routes
    const allPoints: [number, number][] = [[stopLat, stopLng]]
    routesWithStops.forEach(({ stops }) => {
      stops.forEach(s => {
        allPoints.push([parseFloat(s.StopLat), parseFloat(s.StopLng)])
      })
    })

    if (allPoints.length > 1) {
      const bounds = L.latLngBounds(allPoints)
      map.fitBounds(bounds, { padding: [40, 40] })
    } else {
      map.setView([stopLat, stopLng], 15)
    }

    hasFittedInitial.current = true
  }, [stop, routesWithStops, map])

  return null
}

export function StopInfoMap({ stop, routesWithStops, lang }: StopInfoMapProps) {
  const stopLat = parseFloat(stop.stop_lat)
  const stopLng = parseFloat(stop.stop_lng)

  const getStopName = (s: StopInfo) =>
    lang === 'en' ? (s.StopDescrEng || s.StopDescr) : s.StopDescr

  // Create a set of unique stop codes to avoid duplicate markers
  const uniqueStops = useMemo(() => {
    const stopMap = new Map<string, StopInfo>()
    routesWithStops.forEach(({ stops }) => {
      stops.forEach(s => {
        if (!stopMap.has(s.StopCode)) {
          stopMap.set(s.StopCode, s)
        }
      })
    })
    return Array.from(stopMap.values())
  }, [routesWithStops])

  return (
    <div className="map-view-container">
      <MapContainer
        center={[stopLat, stopLng]}
        zoom={15}
        className="map-view"
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBounds stop={stop} routesWithStops={routesWithStops} />

        {/* Draw route lines */}
        {routesWithStops.map(({ route, stops }, index) => {
          const coordinates: [number, number][] = stops.map(s => [
            parseFloat(s.StopLat),
            parseFloat(s.StopLng)
          ])

          if (coordinates.length < 2) return null

          return (
            <Polyline
              key={route.RouteCode}
              positions={coordinates}
              pathOptions={{
                color: ROUTE_COLORS[index % ROUTE_COLORS.length],
                weight: 4,
                opacity: 0.7,
                lineCap: 'round',
                lineJoin: 'round'
              }}
            />
          )
        })}

        {/* Draw stop markers (excluding the selected stop) */}
        {uniqueStops.map(s => {
          if (s.StopCode === stop.stop_code) return null
          return (
            <Marker
              key={s.StopCode}
              position={[parseFloat(s.StopLat), parseFloat(s.StopLng)]}
              icon={stopIcon}
            >
              <Tooltip direction="top" offset={[0, -10]} className="map-tooltip">
                <div className="tooltip-content">
                  <span className="tooltip-name">{getStopName(s)}</span>
                </div>
              </Tooltip>
            </Marker>
          )
        })}

        {/* Draw selected stop marker (on top) */}
        <Marker
          position={[stopLat, stopLng]}
          icon={selectedStopIcon}
        >
          <Tooltip direction="top" offset={[0, -14]} className="map-tooltip" permanent>
            <div className="tooltip-content">
              <span className="tooltip-name">
                {lang === 'en' ? (stop.stop_descr_eng || stop.stop_descr) : stop.stop_descr}
              </span>
            </div>
          </Tooltip>
        </Marker>
      </MapContainer>
    </div>
  )
}
